import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, CalendarIcon } from "lucide-react"
import { useBillingStatus } from '@/hooks/useBillingStatus'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { toast } from '@/components/ui/use-toast'
import { supabase } from '@/lib/supabase'
import { Link } from 'react-router-dom'
import { Logos3 } from "@/components/ui/logos3"
import PricingPlansDisplay from '@/components/shared/PricingPlansDisplay'

interface Plan {
    id: string
    product_name: string
    product_description: string
    price: number
    currency: string
    interval: 'month' | 'year'
    features: (string | { name: string; })[]
    structured_features?: {
        max_brands?: string
        monthly_credits?: string
        features?: string
        [key: string]: any
    }
}

export default function SubscriptionSettings() {
    const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly')
    const [confirmationDialog, setConfirmationDialog] = useState<{
        isOpen: boolean
        planId: string
        planName: string
        isUpgrade: boolean
        effectiveDate?: string
        isNewSubscription?: boolean
    }>({
        isOpen: false,
        planId: '',
        planName: '',
        isUpgrade: false
    })
    const [isChanging, setIsChanging] = useState(false)
    const [plans, setPlans] = useState<Plan[]>([])
    const [plansLoading, setPlansLoading] = useState(true)

    const {
        loading,
        error,
        billingStatus,
        handleChangePlan
    } = useBillingStatus()

    // Fetch plans on component mount
    useEffect(() => {
        const fetchPlans = async () => {
            try {
                setPlansLoading(true)
                const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }
                
                if (!account?.account_id) {
                    throw new Error('No account found')
                }

                const session = await supabase.auth.getSession()
                if (!session.data.session) {
                    throw new Error('No session found')
                }

                const workerUrl = import.meta.env.VITE_WORKERS_URL
                const response = await fetch(`${workerUrl}/billing-functions/get_plans_public`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${session.data.session.access_token}`
                    },
                    body: JSON.stringify({ account_id: account.account_id })
                })

                if (!response.ok) {
                    throw new Error('Failed to fetch plans')
                }

                const data = await response.json()
                // Transform the plan details into the format expected by groupPlans
                const transformedPlans = data.map((plan: any) => ({
                    id: plan.id,
                    product_name: plan.product_name,
                    product_description: plan.product_description,
                    price: plan.price,
                    currency: plan.currency,
                    interval: plan.interval === 'month' ? 'month' : 'year',
                    features: plan.features,
                    structured_features: plan.structured_features || {}
                }))
                setPlans(transformedPlans)
            } catch (error) {
                console.error('Error fetching plans:', error)
                toast({
                    title: "Error",
                    description: "Failed to load subscription plans",
                    variant: "destructive"
                })
            } finally {
                setPlansLoading(false)
            }
        }

        fetchPlans()
    }, [])

    const handlePlanSelection = async (planId: string, planName: string, _planPrice: number, _isEnterprise?: boolean) => {
        const selectedPlan = plans.find(p => p.id === planId)
        const currentPlanId = billingStatus?.subscription?.plan_id
        const currentPlan = currentPlanId ? plans.find(p => p.id === currentPlanId) : null
        
        // If no current plan, this is a new subscription
        if (!currentPlan) {
            setConfirmationDialog({
                isOpen: true,
                planId,
                planName,
                isUpgrade: false,
                isNewSubscription: true
            })
            return
        }

        if (!selectedPlan) return

        const isUpgrade = (selectedPlan.price || 0) > (currentPlan.price || 0)

        setConfirmationDialog({
            isOpen: true,
            planId,
            planName,
            isUpgrade,
            isNewSubscription: false
        })
    }

    const handleConfirmChange = async () => {
        setIsChanging(true)
        try {
            const result = await handleChangePlan(
                confirmationDialog.planId,
                billingInterval,
                { billing_cycle_anchor: confirmationDialog.isUpgrade ? 'now' : 'unchanged' }
            )

            if (result.error) {
                throw new Error(result.error)
            }

            // For new subscriptions, redirect to Stripe Checkout
            if (confirmationDialog.isNewSubscription && result.checkout_url) {
                window.location.href = result.checkout_url
                return
            }

            // For existing subscriptions, show success message
            toast({
                title: confirmationDialog.isNewSubscription ? "Subscription created" : "Subscription updated",
                description: confirmationDialog.isNewSubscription 
                    ? "Your subscription has been set up successfully."
                    : (confirmationDialog.isUpgrade 
                        ? "Your upgrade has been processed and is effective immediately."
                        : "Your downgrade will take effect at the end of your current billing period.")
            })
            setConfirmationDialog(prev => ({ ...prev, isOpen: false }))
        } catch (error: any) {
            toast({
                title: "Error updating subscription",
                description: error.message,
                variant: "destructive"
            })
        } finally {
            setIsChanging(false)
        }
    }

    if (!billingStatus) {
        return null
    }

    if (plansLoading || loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="text-red-500 p-4">
                Error loading subscription data: {error.message}
            </div>
        )
    }

    if (!billingStatus?.has_active_subscription && billingStatus?.subscription?.status !== 'trialing') {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Subscription Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p>You don't have an active subscription.</p>
                    <Link to="/onboarding?step=plan">
                        <Button>View Plans</Button>
                    </Link>
                </CardContent>
            </Card>
        )
    }

    return (
        <div className="space-y-8 pt-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {billingStatus?.subscription?.status === 'trialing' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <CalendarIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">Trial Period Active</h3>
                            <div className="mt-2 text-sm text-blue-700">
                                <p>
                                    {billingStatus.subscription.trial_end ? (
                                        `Your trial ends on ${new Date(billingStatus.subscription.trial_end).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}. ${Math.ceil((new Date(billingStatus.subscription.trial_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days remaining.`
                                    ) : 'Your trial is currently active.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="text-center space-y-6 mb-8">
                <h2 className="text-3xl md:text-4xl font-bold">Plans to maximize your growth</h2>
            </div>

            <PricingPlansDisplay
                plans={plans}
                billingInterval={billingInterval}
                onBillingIntervalChange={setBillingInterval}
                onPlanSelect={handlePlanSelection}
                currentPlanId={billingStatus?.subscription?.plan_id}
                isChangingPlan={true}
                loading={plansLoading}
                showComparisonTable={true}
                variant="settings"
            />

            <Dialog open={confirmationDialog.isOpen} onOpenChange={(isOpen) => setConfirmationDialog(prev => ({ ...prev, isOpen }))}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {confirmationDialog.isNewSubscription 
                                ? `Subscribe to ${confirmationDialog.planName}`
                                : (confirmationDialog.isUpgrade 
                                    ? `Upgrade to ${confirmationDialog.planName}` 
                                    : `Downgrade to ${confirmationDialog.planName}`)}
                        </DialogTitle>
                        <DialogDescription>
                            {confirmationDialog.isNewSubscription ? (
                                'You will be charged according to the plan terms. New customers may be eligible for a free trial.'
                            ) : (
                                confirmationDialog.isUpgrade ? (
                                    'Your upgrade will be effective immediately and you will be charged a prorated amount for the remainder of your billing period.'
                                ) : (
                                    'Your downgrade will take effect at the end of your current billing period. You will continue to have access to your current plan\'s features until then.'
                                )
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setConfirmationDialog(prev => ({ ...prev, isOpen: false }))}
                            disabled={isChanging}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmChange}
                            disabled={isChanging}
                        >
                            {isChanging ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                confirmationDialog.isNewSubscription ? 'Subscribe' : 'Confirm Change'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="text-center space-y-6 pt-12">
                <Logos3 />
            </div>
        </div>
    )
} 